import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  alpha,
  useTheme,
  Collapse,
  Grid,
} from "@mui/material";
import {
  Build as BuildIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
} from "@mui/icons-material";

/**
 * Dialog showing reconstruction history for a damaged check record
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - checkRecord: The damaged check record
 * - reconstructions: Array of reconstruction records
 * - onUpdateStatus: function(reconstructionId, status) - callback
 */
function ReconstructionHistoryDialog({
  open,
  onClose,
  checkRecord,
  reconstructions = [],
  onUpdateStatus,
}) {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState({});

  const handleToggleRow = (reconstructionId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [reconstructionId]: !prev[reconstructionId],
    }));
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      IN_PROGRESS: {
        color: theme.palette.warning.main,
        bgColor: alpha(theme.palette.warning.main, 0.1),
        icon: <PendingIcon sx={{ fontSize: 16 }} />,
        label: "In Progress",
      },
      COMPLETED: {
        color: theme.palette.success.main,
        bgColor: alpha(theme.palette.success.main, 0.1),
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
        label: "Completed",
      },
      CANCELLED: {
        color: theme.palette.error.main,
        bgColor: alpha(theme.palette.error.main, 0.1),
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
        label: "Cancelled",
      },
    };

    const config = statusConfig[status] || statusConfig.IN_PROGRESS;

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgColor,
          color: config.color,
          fontWeight: 600,
        }}
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleUpdateStatus = (reconstructionId, newStatus) => {
    if (onUpdateStatus) {
      onUpdateStatus(reconstructionId, newStatus);
    }
  };

  if (!checkRecord) return null;

  const lineItem = checkRecord.receive?.subOrderLineItem;
  const totalDamaged = checkRecord.damagedQty || 0;
  const totalReconstructed = reconstructions.reduce(
    (sum, r) => sum + (r.reconstructedQty || 0),
    0
  );
  const remaining = totalDamaged - totalReconstructed;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: "70vh" },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BuildIcon color="primary" />
            <Typography variant="h6">Reconstruction History</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Summary Section */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Damaged Items Summary
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Item
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {lineItem?.imports?.itemName || "Unknown"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lineItem?.imports?.brand || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Total Damaged
              </Typography>
              <Typography variant="h6" color="error.main" fontWeight="700">
                {totalDamaged}
              </Typography>
            </Grid>
            <Grid item xs={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                In Reconstruction
              </Typography>
              <Typography variant="h6" color="warning.main" fontWeight="700">
                {totalReconstructed}
              </Typography>
            </Grid>
            <Grid item xs={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Remaining
              </Typography>
              <Typography variant="h6" color="success.main" fontWeight="700">
                {remaining}
              </Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="caption" color="text.secondary">
                Check Batch
              </Typography>
              <Typography variant="body1" fontWeight="600">
                #{checkRecord.id}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Reconstruction Records Table */}
        {reconstructions && reconstructions.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                >
                  <TableCell width="50px"></TableCell>
                  <TableCell>
                    <strong>ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Quantity</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Reconstructed By</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Date</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reconstructions.map((reconstruction) => (
                  <React.Fragment key={reconstruction.id}>
                    <TableRow
                      hover
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleRow(reconstruction.id)}
                        >
                          {expandedRows[reconstruction.id] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>#{reconstruction.id}</TableCell>
                      <TableCell>
                        <Typography fontWeight="600">
                          {reconstruction.reconstructedQty}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(reconstruction.status)}
                      </TableCell>
                      <TableCell>{reconstruction.reconstructedBy}</TableCell>
                      <TableCell>
                        {formatDate(reconstruction.reconstructedDate)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          {reconstruction.status === "IN_PROGRESS" && (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={() =>
                                  handleUpdateStatus(
                                    reconstruction.id,
                                    "COMPLETED"
                                  )
                                }
                              >
                                Complete
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  handleUpdateStatus(
                                    reconstruction.id,
                                    "CANCELLED"
                                  )
                                }
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          {reconstruction.status !== "IN_PROGRESS" && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {reconstruction.status === "COMPLETED"
                                ? "Finished"
                                : "Cancelled"}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row - Input Materials and Notes */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0 }}>
                        <Collapse
                          in={expandedRows[reconstruction.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: alpha(theme.palette.grey[500], 0.03),
                            }}
                          >
                            {/* Input Materials */}
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              fontWeight="600"
                            >
                              Input Materials Used
                            </Typography>

                            {reconstruction.inputs &&
                            reconstruction.inputs.length > 0 ? (
                              <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{ mb: 2 }}
                              >
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Item</TableCell>
                                      <TableCell>Brand</TableCell>
                                      <TableCell align="right">
                                        Quantity
                                      </TableCell>
                                      <TableCell>Unit</TableCell>
                                      <TableCell align="right">
                                        Unit Amount
                                      </TableCell>
                                      <TableCell align="right">
                                        Total Units
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {reconstruction.inputs.map((input, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          {input.imports?.itemName || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                          {input.imports?.brand || "N/A"}
                                        </TableCell>
                                        <TableCell align="right">
                                          <strong>{input.qty}</strong>
                                        </TableCell>
                                        <TableCell>{input.itemUnit}</TableCell>
                                        <TableCell align="right">
                                          {input.prodUnitsForItemUnit}
                                        </TableCell>
                                        <TableCell align="right">
                                          {(
                                            input.qty *
                                            input.prodUnitsForItemUnit
                                          ).toFixed(2)}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                              >
                                No input materials recorded (manual
                                reconstruction)
                              </Typography>
                            )}

                            {/* Notes */}
                            {reconstruction.notes && (
                              <Box>
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  fontWeight="600"
                                >
                                  Notes
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {reconstruction.notes}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <BuildIcon
              sx={{
                fontSize: 64,
                color: "text.secondary",
                mb: 2,
                opacity: 0.3,
              }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Reconstruction Records
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No reconstruction attempts have been made for these damaged items
              yet.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReconstructionHistoryDialog;
